import { matchPath } from 'react-router-dom';

const NO_NAVIGATION = Object.freeze({ desktop: false, mobile: false });

function interpolateRoutePath(path, params = {}) {
  return path.replace(/:([A-Za-z0-9_]+)/g, (_, key) => {
    const value = params[key];
    if (value === undefined || value === null || value === '') {
      throw new Error(`Missing route parameter: ${key}`);
    }
    return encodeURIComponent(String(value));
  });
}

function defineRoute(id, path, componentId, options = {}) {
  const layout = path.startsWith('/auth/') ? 'auth' : 'empresa';
  return Object.freeze({
    id,
    path,
    componentId,
    layout,
    labelKey: null,
    fallbackName: id,
    section: 'hidden',
    iconKey: null,
    navigationKey: null,
    scope: layout === 'auth' ? 'public' : 'account',
    entitlement: null,
    discoverableWhenLocked: false,
    navigation: NO_NAVIGATION,
    chrome: 'hidden',
    order: 0,
    mobileOrder: null,
    buildPath: (params) => interpolateRoutePath(path, params),
    ...options,
  });
}

const nav = (desktop = false, mobile = false) =>
  Object.freeze({ desktop, mobile });

export const CLIENT_ROUTE_MANIFEST = Object.freeze([
  defineRoute('sign_in', '/auth/sign-in/default', 'signIn'),
  defineRoute('sign_up', '/auth/sign-up/default', 'signUp'),
  defineRoute('home', '/empresa/home', 'companyHomeRedirect', {
    labelKey: 'navigation.home',
    fallbackName: 'Home',
  }),
  defineRoute('create_company', '/empresa/nueva', 'userNew', {
    labelKey: 'navigation.createCompany',
    fallbackName: 'Formar Empresa',
    section: 'company_actions',
    iconKey: 'newCompany',
    navigationKey: 'new-company',
    navigation: nav(false, 'sheet'),
    chrome: 'wizard',
    order: 10,
    mobileOrder: 10,
  }),
  defineRoute('import_company', '/empresa/import/new', 'importWizard', {
    labelKey: 'navigation.importCompany',
    fallbackName: 'Import Company',
    section: 'company_actions',
    iconKey: 'importCompany',
    navigationKey: 'import-company',
    navigation: nav(false, 'sheet'),
    chrome: 'wizard',
    order: 20,
    mobileOrder: 20,
  }),
  defineRoute('imports', '/empresa/imports', 'importsList', {
    labelKey: 'navigation.myImports',
    fallbackName: 'My Imports',
    chrome: 'empresa_surface',
  }),
  defineRoute('import_detail', '/empresa/import/:importId', 'importWizard', {
    labelKey: 'navigation.importDetail',
    fallbackName: 'Import Detail',
    chrome: 'wizard',
  }),
  defineRoute('companies', '/empresa/empresas', 'companies', {
    labelKey: 'navigation.companies',
    fallbackName: 'Empresas',
  }),
  defineRoute('design_samples', '/empresa/design-samples', 'designSamples', {
    fallbackName: 'Design Samples',
  }),
  defineRoute(
    'company_profile',
    '/empresa/empresas/:registrationId/perfil',
    'companyProfile',
    {
      labelKey: 'navigation.companyProfile',
      fallbackName: 'Perfil de Empresa',
      chrome: 'company_surface',
    }
  ),
  defineRoute(
    'company_dashboard',
    '/empresa/:companyId/dashboard',
    'dashboard',
    {
      labelKey: 'navigation.dashboard',
      mobileLabelKey: 'navigation.dashboardShort',
      fallbackName: 'Company record',
      section: 'primary',
      iconKey: 'passport',
      scope: 'company',
      navigation: nav(true, 'tab'),
      chrome: 'company_surface',
      order: 10,
      mobileOrder: 10,
    }
  ),
  defineRoute(
    'company_payment',
    '/empresa/:companyId/payment',
    'formationPayment',
    {
      fallbackName: 'Formation payment',
      scope: 'company',
      chrome: 'transactional',
    }
  ),
  defineRoute(
    'company_notifications',
    '/empresa/:companyId/notificaciones',
    'notifications',
    {
      labelKey: 'navigation.notifications',
      mobileLabelKey: 'navigation.notificationsShort',
      fallbackName: 'Notificaciones',
      section: 'hidden',
      iconKey: 'signals',
      scope: 'company',
      chrome: 'company_surface',
    }
  ),
  defineRoute(
    'notifications',
    '/empresa/notificaciones',
    'notificationsRedirect',
    {
      labelKey: 'navigation.notifications',
      fallbackName: 'Notificaciones',
      chrome: 'empresa_surface',
    }
  ),
  defineRoute(
    'company_documents',
    '/empresa/:companyId/documentos',
    'documents',
    {
      labelKey: 'navigation.documents',
      mobileLabelKey: 'navigation.documentsShort',
      fallbackName: 'Documentos',
      section: 'primary',
      iconKey: 'evidence',
      scope: 'company',
      navigation: nav(true, 'tab'),
      chrome: 'company_surface',
      order: 30,
      mobileOrder: 30,
    }
  ),
  defineRoute('company_banking', '/empresa/:companyId/banking', 'banking', {
    labelKey: 'navigation.bankAccount',
    mobileLabelKey: 'navigation.bankAccountShort',
    fallbackName: 'Cuenta Bancaria',
    section: 'finance',
    iconKey: 'accounts',
    scope: 'company',
    navigation: nav(true, 'tab'),
    chrome: 'company_surface',
    order: 20,
    mobileOrder: 40,
  }),
  defineRoute('company_lili', '/empresa/:companyId/lili-onboarding', 'lili', {
    labelKey: 'navigation.liliOnboarding',
    fallbackName: 'Lili application',
    scope: 'company',
    chrome: 'transactional',
  }),
  defineRoute('company_command', '/empresa/:companyId/command', 'command', {
    labelKey: 'navigation.relyCommand',
    mobileLabelKey: 'navigation.commandShort',
    fallbackName: 'Operating center',
    section: 'primary',
    iconKey: 'command',
    scope: 'company',
    entitlement: 'invoicing_membership',
    feature: 'rely_command_preview',
    navigation: nav(true, 'tab'),
    chrome: 'company_surface',
    order: 20,
    mobileOrder: 20,
  }),
  defineRoute(
    'company_invoices',
    '/empresa/:companyId/invoices',
    'invoicesCommand',
    {
      labelKey: 'navigation.invoices',
      fallbackName: 'Invoices',
      section: 'finance',
      iconKey: 'invoices',
      scope: 'company',
      entitlement: 'invoicing_membership',
      discoverableWhenLocked: true,
      navigation: nav(true, 'sheet'),
      chrome: 'company_surface',
      order: 10,
      mobileOrder: 30,
    }
  ),
  defineRoute(
    'company_monthly_close',
    '/empresa/:companyId/back-office/cierre-mensual',
    'monthlyClose',
    {
      labelKey: 'navigation.monthlyClose',
      fallbackName: 'Cierre mensual',
      section: 'finance',
      iconKey: 'monthlyClose',
      scope: 'company',
      entitlement: 'invoicing_membership',
      discoverableWhenLocked: true,
      navigation: nav(true, 'sheet'),
      chrome: 'company_surface',
      order: 40,
      mobileOrder: 50,
    }
  ),
  defineRoute(
    'command_approvals',
    '/empresa/:companyId/command/approvals',
    'command',
    {
      labelKey: 'navigation.commandApprovals',
      fallbackName: 'Command Approvals',
      scope: 'company',
      chrome: 'company_surface',
    }
  ),
  defineRoute(
    'command_integrations',
    '/empresa/:companyId/command/integrations',
    'command',
    {
      labelKey: 'navigation.commandIntegrations',
      fallbackName: 'Command Integrations',
      scope: 'company',
      chrome: 'company_surface',
    }
  ),
  defineRoute(
    'command_payments',
    '/empresa/:companyId/command/payments',
    'command',
    {
      labelKey: 'navigation.commandPayments',
      fallbackName: 'Command Payments',
      scope: 'company',
      chrome: 'company_surface',
    }
  ),
  defineRoute(
    'command_studio',
    '/empresa/:companyId/command/studio',
    'command',
    {
      labelKey: 'navigation.commandStudio',
      fallbackName: 'Command Studio',
      scope: 'company',
      chrome: 'company_surface',
    }
  ),
  defineRoute(
    'command_banking',
    '/empresa/:companyId/command/banking',
    'command',
    {
      labelKey: 'navigation.commandBanking',
      fallbackName: 'Command Banking',
      scope: 'company',
      chrome: 'company_surface',
    }
  ),
  defineRoute(
    'command_lili',
    '/empresa/:companyId/command/banking/lili',
    'command',
    {
      labelKey: 'navigation.commandLili',
      fallbackName: 'Command Lili',
      scope: 'company',
      chrome: 'transactional',
    }
  ),
  defineRoute('legacy_banking', '/empresa/banking', 'bankingRedirect', {
    labelKey: 'navigation.bankAccount',
    fallbackName: 'Cuenta Bancaria',
    chrome: 'empresa_surface',
  }),
  defineRoute('company_taxes', '/empresa/:companyId/impuestos', 'taxes', {
    labelKey: 'navigation.taxes',
    fallbackName: 'Impuestos',
    section: 'finance',
    iconKey: 'compliance',
    scope: 'company',
    navigation: nav(true, 'sheet'),
    chrome: 'company_surface',
    order: 30,
    mobileOrder: 40,
  }),
  defineRoute(
    'company_member_changes',
    '/empresa/:companyId/servicios/miembros',
    'memberChanges',
    {
      fallbackName: 'Editar Miembros',
      scope: 'company',
      chrome: 'transactional',
    }
  ),
  defineRoute(
    'company_ein',
    '/empresa/:companyId/servicios/ein',
    'einRequest',
    {
      fallbackName: 'EIN',
      scope: 'company',
      chrome: 'transactional',
    }
  ),
  defineRoute(
    'company_service_requests',
    '/empresa/:companyId/servicios/solicitudes',
    'serviceRequests',
    { fallbackName: 'Solicitudes', scope: 'company', chrome: 'transactional' }
  ),
  defineRoute('legacy_taxes', '/empresa/impuestos', 'taxes', {
    labelKey: 'navigation.taxes',
    fallbackName: 'Impuestos',
    chrome: 'empresa_surface',
  }),
  defineRoute('company_perks', '/empresa/:companyId/perks', 'perks', {
    labelKey: 'navigation.perks',
    fallbackName: 'Perks',
    section: 'resources',
    iconKey: 'benefits',
    scope: 'company',
    navigation: nav(false, 'sheet'),
    chrome: 'company_surface',
    order: 10,
    mobileOrder: 60,
  }),
  defineRoute('support', '/empresa/soporte', 'support', {
    labelKey: 'help',
    sheetLabelKey: 'navigation.helpSupport',
    fallbackName: 'Ayuda',
    section: 'account',
    iconKey: 'support',
    navigation: nav(true, 'sheet'),
    chrome: 'empresa_surface',
    order: 10,
    mobileOrder: 80,
  }),
  defineRoute('referrals', '/empresa/referidos', 'referrals', {
    labelKey: 'navigation.referrals',
    fallbackName: 'Referidos',
    section: 'account',
    iconKey: 'referrals',
    navigation: nav(true, 'sheet'),
    chrome: 'empresa_surface',
    order: 5,
    mobileOrder: 70,
  }),
  defineRoute('settings', '/empresa/cuenta', 'settings', {
    labelKey: 'settings',
    fallbackName: 'Configuracion',
    section: 'account',
    iconKey: 'settings',
    navigation: nav(true, 'sheet'),
    chrome: 'empresa_surface',
    order: 20,
    mobileOrder: 90,
  }),
]);

const routeById = new Map(
  CLIENT_ROUTE_MANIFEST.map((route) => [route.id, route])
);

export function getClientRoute(id) {
  const route = routeById.get(id);
  if (!route) throw new Error(`Unknown client route: ${id}`);
  return route;
}

export function buildClientRoutePath(id, params) {
  return getClientRoute(id).buildPath(params);
}

export function getClientRouterPath(id) {
  const route = getClientRoute(id);
  return route.path.replace(`/${route.layout}/`, '');
}

export function getClientNavigationItems({
  placement,
  section,
  companyId,
  entitlements = [],
  features = [],
} = {}) {
  const enabledEntitlements = new Set(entitlements);
  const enabledFeatures = new Set(features);
  const hasRequiredEntitlement = (route) =>
    !route.entitlement || enabledEntitlements.has(route.entitlement);

  return CLIENT_ROUTE_MANIFEST.filter((route) => {
    const exposure =
      placement === 'desktop'
        ? route.navigation.desktop
        : route.navigation.mobile === placement?.replace('mobile-', '');
    return (
      exposure &&
      (hasRequiredEntitlement(route) || route.discoverableWhenLocked) &&
      (!route.feature || enabledFeatures.has(route.feature)) &&
      (!section || route.section === section)
    );
  })
    .sort((left, right) => {
      if (placement?.startsWith('mobile-')) {
        return (
          (left.mobileOrder ?? left.order) - (right.mobileOrder ?? right.order)
        );
      }
      return left.order - right.order;
    })
    .map((route) => {
      const disabled = route.scope === 'company' && !companyId;
      return {
        ...route,
        disabled,
        locked: Boolean(
          route.discoverableWhenLocked && !hasRequiredEntitlement(route)
        ),
        href: disabled ? undefined : route.buildPath({ companyId }),
      };
    });
}

export function getClientRouteChrome(
  pathname,
  { layoutType = 'empresa' } = {}
) {
  if (layoutType !== 'empresa') {
    return {
      routeId: null,
      routeKind: 'admin',
      showMobileDock: false,
      reserveDockClearance: false,
    };
  }

  const route = CLIENT_ROUTE_MANIFEST.find((candidate) =>
    matchPath({ path: candidate.path, end: true }, pathname || '/')
  );
  const routeKind = route?.chrome || 'empresa';
  const showMobileDock = ['company_surface', 'empresa_surface'].includes(
    routeKind
  );

  return {
    routeId: route?.id || null,
    routeKind,
    showMobileDock,
    reserveDockClearance: showMobileDock,
  };
}
